// `ClientContactAsyncContainerBase` and its two subclasses
// (`services/resolvers/client-contact/client-contact-{async-service-base,alert-async-service.resolver,profile-async-service.resolver}.ng1.ts`)
// — the per-screen registry of a contact's long-running operations, and the poll that resolves them.
//
// A snippet that STARTS an operation registers it here (`onInitiateAsyncEventFromSnippet`) with a
// callback; a snippet that OPENS while one is already running finds it in the pending list the
// container loaded on init, and subscribes. Every two seconds, while anything is pending, the
// container asks `getCompletedOperationResults` for those message ids and hands each finished one
// to whichever snippet is subscribed for that contact.
//
// ⚠️ ONLY CREATE_CLIENT_CONTACT_IN_ATS AND UPDATE_CLIENT_CONTACT_IN_ATS HAVE A CONSUMER HERE. The
// third type, GET_LATEST_DETAILS ("Get phone and email"), is carried through unchanged — it is
// polled, completed and dispatched exactly as upstream does — but the merge of its result into the
// contact (`ClientContactDetailsUtils.updateClientContactDetails`) is the caller's, through
// `mergeLatestDetails`, because the enrichment flow that would supply it is not ported.
import {
  ClientContactAsyncOperationType,
  getClientContactCompletedOperationResults,
  getClientContactPendingOperationsByAlertId,
  getClientContactPendingOperationsByClientContactIds,
  type ClientContactCompletedOperation,
  type ClientContactCompletedOperationsResponse,
  type ClientContactPendingOperation,
  type ClientContactPendingOperationsResponse,
} from '@woo-ext/api'

export type ClientContactAsyncOperationHandler = (operation: ClientContactCompletedOperation) => void
export type FilterResultsCallback = (results: ClientContactCompletedOperation[]) => ClientContactCompletedOperation[]

/** The three calls, injectable so the poll is testable without a network. */
export interface ClientContactAsyncOperationsApi {
  getPendingOperationsByAlertId: (params: { alertId: number }) => Promise<ClientContactPendingOperationsResponse | null>
  getPendingOperationsByClientContactIds: (params: {
    clientContactIds: number[]
  }) => Promise<ClientContactPendingOperationsResponse | null>
  getCompletedOperationResults: (params: { messageIds: string[] }) => Promise<ClientContactCompletedOperationsResponse | null>
}

export const defaultClientContactAsyncOperationsApi: ClientContactAsyncOperationsApi = {
  getPendingOperationsByAlertId: getClientContactPendingOperationsByAlertId,
  getPendingOperationsByClientContactIds: getClientContactPendingOperationsByClientContactIds,
  getCompletedOperationResults: getClientContactCompletedOperationResults,
}

/** `pendingOperationsIntervalTime`. */
export const PENDING_OPERATIONS_INTERVAL_MS = 2000

export interface ClientContactAsyncContainerOptions {
  api?: ClientContactAsyncOperationsApi
  /** `handleClientContactDetailsAsyncOperation` — see the note at the top of this file. */
  mergeLatestDetails?: (clientContactId: number, result: unknown) => void
  setInterval?: (callback: () => void, ms: number) => unknown
  clearInterval?: (handle: unknown) => void
}

export class ClientContactAsyncContainer {
  private pendingOperations: ClientContactPendingOperation[] = []
  private readonly handlers: Record<number, ClientContactAsyncOperationHandler> = {}
  private interval: unknown = undefined
  /** `completedOperationsResponsePromise` — the in-flight guard. */
  private polling = false
  private filterResults: FilterResultsCallback = (results) => results
  private readonly api: ClientContactAsyncOperationsApi
  private readonly options: ClientContactAsyncContainerOptions

  constructor(options: ClientContactAsyncContainerOptions = {}) {
    this.options = options
    this.api = options.api ?? defaultClientContactAsyncOperationsApi
  }

  /**
   * `ClientContactAlertAsyncContainer.initClientContactPendingEventsMap` — the alert's operations.
   * ⚠️ `alertId <= 0` loads nothing: the alertless sentinel ids are not alerts.
   */
  async initForAlert(alertId: number): Promise<void> {
    if (alertId <= 0) return
    const response = await this.api.getPendingOperationsByAlertId({ alertId })
    if (!response?.operations) return
    this.pendingOperations = response.operations
  }

  /** `ClientContactProfileAsyncContainer.initClientContactPendingEventsMap` — one contact's. */
  async initForClientContact(clientContactId: number): Promise<void> {
    const response = await this.api.getPendingOperationsByClientContactIds({ clientContactIds: [clientContactId] })
    if (!response?.operations) return
    this.pendingOperations = response.operations
  }

  /**
   * `addToPendingEventsByClientContactIds` — operations logged for a contact WITHOUT this alert
   * that still appear in its results; merged by id, never duplicated.
   */
  async addToPendingEventsByClientContactIds(clientContactIds: number[]): Promise<void> {
    const response = await this.api.getPendingOperationsByClientContactIds({ clientContactIds })
    if (!response?.operations) return
    const existing = new Set(this.pendingOperations.map((operation) => operation.id))
    this.pendingOperations.push(...response.operations.filter((operation) => !existing.has(operation.id)))
  }

  getClientContactPendingOperations(): readonly ClientContactPendingOperation[] {
    return this.pendingOperations
  }

  removePendingOperationsByIds(ids: readonly string[]): void {
    const remove = new Set(ids)
    this.pendingOperations = this.pendingOperations.filter((operation) => !remove.has(operation.id))
  }

  /**
   * ⚠️ ONE HANDLER PER CONTACT, AND THE CANCELLER DELETES WHATEVER IS THERE. Upstream keys the map
   * by contact id and its canceller is `delete map[id]` — so a stale canceller removes a newer
   * subscription for the same contact. Kept: only one snippet per contact is ever open.
   */
  subscribeToAsyncEventFromSnippet(clientContactId: number, handler: ClientContactAsyncOperationHandler): () => void {
    this.handlers[clientContactId] = handler
    return () => {
      delete this.handlers[clientContactId]
    }
  }

  onInitiateAsyncEventFromSnippet(
    event: ClientContactPendingOperation,
    handler: ClientContactAsyncOperationHandler,
  ): () => void {
    this.pendingOperations.push(event)
    return this.subscribeToAsyncEventFromSnippet(event.clientContactId, handler)
  }

  runPostOperationClientContactSnippetCallback(clientContactId: number, operation: ClientContactCompletedOperation): void {
    this.handlers[clientContactId]?.(operation)
  }

  /** `initPendingEventsInterval`. The contact-lookup half is `mergeLatestDetails`. */
  startPolling(filterResults: FilterResultsCallback = (results) => results): void {
    this.filterResults = filterResults
    if (this.interval !== undefined) return
    const schedule = this.options.setInterval ?? ((callback: () => void, ms: number) => setInterval(callback, ms))
    this.interval = schedule(() => {
      void this.handlePendingOperations()
    }, PENDING_OPERATIONS_INTERVAL_MS)
  }

  destroy(): void {
    if (this.interval === undefined) return
    const cancel = this.options.clearInterval ?? ((handle: unknown) => clearInterval(handle as ReturnType<typeof setInterval>))
    cancel(this.interval)
    this.interval = undefined
  }

  /** One tick of the poll. Public so a test can drive it without a timer. */
  async handlePendingOperations(): Promise<void> {
    // Still working on the previous tick: no use in running it again.
    if (this.polling) return
    if (this.pendingOperations.length === 0) return

    this.polling = true
    let response: ClientContactCompletedOperationsResponse | null
    try {
      response = await this.api.getCompletedOperationResults({ messageIds: this.pendingOperations.map((op) => op.id) })
    } catch {
      this.polling = false
      return
    }
    const results = response?.results
    if (!results || results.length === 0) {
      this.polling = false
      return
    }
    // ⚠️ THE FINALLY MATTERS (upstream's own comment): if a handler throws, the in-flight guard
    // must still be released, or the poller silently stops for every contact on the page.
    try {
      this.filterResults(results).forEach((operation) => {
        this.handlePendingOperationByType(operation)
      })
    } finally {
      this.polling = false
    }
  }

  private handlePendingOperationByType(operation: ClientContactCompletedOperation): void {
    switch (operation.operationType) {
      case ClientContactAsyncOperationType.GET_LATEST_DETAILS:
        // A failed operation is terminal but carries no result — nothing to merge.
        if (operation.result != null) this.options.mergeLatestDetails?.(operation.clientContactId, operation.result)
        break
      case ClientContactAsyncOperationType.CREATE_CLIENT_CONTACT_IN_ATS:
        // No need to update the contact on the alert/search results level.
        break
      default:
        break
    }
    // If a snippet is open while an operation is being handled, run its registered callback.
    this.runPostOperationClientContactSnippetCallback(operation.clientContactId, operation)
    this.removePendingOperationsByIds([operation.id])
  }
}
